import { useEffect } from "react";
import { unitOfTime } from "moment";
import { useAPIRange } from "../../shared/customHoooks/useAPI";
import { message } from "antd";
import {
  getDateFromRelativeDateRange,
  RelativeRangeValue,
} from "../../COREDesignSystem/Form/CORERangePicker";
import { Axis, By, Trace } from "./AggregatesChartV2";
import { RpcAggregatesService as RpcAggregatesServiceAdmin } from "../../openapi-typescript/admin";
import { RpcAggregatesService as RpcAggregatesServiceApproved } from "../../openapi-typescript/approved";
import { NonEmptyRange, Range } from "../../shared/date/ranges";
import { getTechColour } from "../../shared/projectTechnology/technologyColours";
import {
  NEM_STATE_COLOURS,
  NEMState,
} from "../../shared/nemState/nemStateColours";

const { postRpcAggregates: postRpcAdmin } = RpcAggregatesServiceAdmin;
const { postRpcAggregates: postRpcApproved } = RpcAggregatesServiceApproved;

export type AggType = Exclude<
  Parameters<typeof postRpcAdmin>[0] | Parameters<typeof postRpcApproved>[0],
  undefined
>;

export type RpcByType = By; // AggType["by"];
export type RpcDateRangeType = AggType["date_range"];
export type RpcAggFuncType = AggType["agg_function"];
export type RpcAggPeriodType = AggType["agg_period"];
export type RpcSelectedItemType = AggType["selected_item"];
export type RpcTableNameType = AggType["table_name"];
export type RpcColumnNameType = AggType["column_name"];

type TreeConfiguration = {
  treeName: string;
  config: {
    key: string;
    tableName: RpcTableNameType;
    columnNames: RpcColumnNameType | null;
    aggFunc: RpcAggFuncType;
    axis: { label: string; side: string };
  }[];
  value: string;
};

export type AGGOptionType = {
  batch: unitOfTime.DurationConstructor;
  value: string;
  label: string;
};
export type AGGFuncOptionType = { value: string; label: string };
type APIOptionType = {
  body: any;
  rangeOptions: {
    range: Range;
    interval: string;
    field: string;
  };
};
type TreeSelectionType = {
  id: number;
  traceName: string;
  variable?: string;
};
export type ChartSelect = {
  tree: string;
  aggPeriod?: RpcAggPeriodType;
  aggFunc?: RpcAggFuncType;
  dateRange: RelativeRangeValue;
  selection: TreeSelectValue;
};
export type ConvertDataType = "positive" | "negative";
export type TracesAPIConfig = {
  chartOptions: Trace;
  params: TraceParam;
};

export type TraceParamsBody = {
  by?: RpcByType;
  aggFunction: RpcAggFuncType;
  aggPeriod: RpcAggPeriodType;
  columnName: RpcColumnNameType;
  dateRange: RpcDateRangeType; //This should be string to support this value "dateRange": "[2021-09-02,2021-09-05)"
  selectedItem: RpcSelectedItemType;
  tableName: RpcTableNameType;
  includeTimesWithoutData?: boolean;
};

// Type for query parameters
type TraceParam = {
  params: TraceParamsBody;
  rangeOptions: { range: NonEmptyRange; interval: string; field: string };
};

export type AggAPI = {
  date: string;
  // eslint-disable-next-line camelcase
  x_axis_label: string | null;
  value: number;
};
export type ChartApiData = {
  data?: (AggAPI | { time: string; value: number; group: number })[];
  chartOptions: Trace;
};

type ChartResponse = {
  error: boolean;
  loading: boolean;
  progress: { percent: number };
  data: ChartApiData[] | undefined;
  aggFunc?: RpcAggFuncType;
};

export type TreeSelectValue = {
  id: number;
  traceName: string;
  variable?: string;
  aemoId?: string;
  shortName?: string;
  region?: string;
  co2eEnergySource?: number;
  // Asset Intelligence technology of a NEM - SCADA selection. Set on technology,
  // sub-technology and asset nodes; absent on the region-level `All {region}`
  // nodes, which colour by state instead.
  technologyType?: string;
  technologySubType?: string | null;
  // Uppercase NEM state code ("QLD") for a region-level selection. Absent for
  // the NEM-wide branch, which has no single state colour.
  stateCode?: NEMState;
  aggPeriod?: RpcAggPeriodType;
  aggFunc?: RpcAggFuncType;
  order?: number;
};
export type TreeSelectionObject = {
  checkedKeys: string[];
  change: TreeSelectValue[];
  value: TreeSelectValue[];
};

/** How a data type's traces are drawn. */
export type TraceStyle = "line" | "bar" | "barStacked" | "area" | "areaStacked";

type DataTypeStyle = {
  traceStyle: TraceStyle;
};

/** Keyed by Y-axis label (see AXIS_LABELS). A missing entry means "line". */
export type DataTypeStyles = Record<string, DataTypeStyle>;

export const defaultTraceStyle: TraceStyle = "line";

export const traceStyleOptions: { value: TraceStyle; label: string }[] = [
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "barStacked", label: "Bar stacked" },
  { value: "area", label: "Area" },
  { value: "areaStacked", label: "Area stacked" },
];

export interface ChartBuilderTreeSelection {
  [key: string]: TreeSelectionObject;
}

/**
 * Every Y-axis label the chart builder can produce. These strings are the
 * canonical "data type" identity: they group traces onto a shared axis and are
 * the keys of `ChartConfig.dataTypeStyles`, so the values must stay
 * byte-identical or saved chart styles orphan.
 */
export const AXIS_LABELS = {
  price: "$/MWh",
  power: "MW",
  energy: "MWh",
  temperature: "°C",
  windSpeed: "m/s",
  solarExposure: "W/m²",
  energyVolume: "Volume (MWh)",
  certificatePrice: "$/certificate",
  certificateVolume: "Volume (Certificates)",
  volatility: "Volatility (%)",
} as const;

const AxisLeft: Axis["side"] = "left";
// Marks an `All {region}` selection — every technology at once. Declared here so
// configTree does not import back into the tree module.
export const regionTotalVariable: string = "region total";
const AxisRight: Axis["side"] = "right";

// Exported so tests can assert the catalogue's invariants (see
// useChartBuilderApiV2.test.ts) without duplicating the label list.
export const configTree: TreeConfiguration[] = [
  {
    treeName: "electricityTree",
    config: [
      {
        key: "price",
        tableName: "dispatch",
        columnNames: "rrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "demand",
        tableName: "dispatch",
        columnNames: "clearedsupply",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "generation",
        tableName: "dispatch",
        columnNames: "dispatchablegeneration",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "availability",
        tableName: "dispatch",
        columnNames: "semischedule_clearedmw",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "rooftop",
        tableName: "rooftop",
        columnNames: "power",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "stpasa demand",
        tableName: "stpasa",
        columnNames: "demand50",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "mtpasa demand",
        tableName: "mtpasa",
        columnNames: "demand50",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
    ],
    value: "aemoId",
  },
  {
    treeName: "FCASTree",
    config: [
      {
        key: "raise 6 sec rrp",
        tableName: "dispatch",
        columnNames: "raise6secrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "raise 60 sec rrp",
        tableName: "dispatch",
        columnNames: "raise60secrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "raise 5 min rrp",
        tableName: "dispatch",
        columnNames: "raise5minrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "raise reg rrp",
        tableName: "dispatch",
        columnNames: "raiseregrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "lower 6 sec rrp",
        tableName: "dispatch",
        columnNames: "lower6secrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "lower 60 sec rrp",
        tableName: "dispatch",
        columnNames: "lower60secrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "lower 5 min rrp",
        tableName: "dispatch",
        columnNames: "lower5minrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "lower reg rrp",
        tableName: "dispatch",
        columnNames: "lowerregrrp",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
    ],
    value: "aemoId",
  },
  {
    treeName: "generationProfileTree",
    config: [
      {
        key: "all",
        tableName: "generation_profile_shapes",
        columnNames: "generation",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energy, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "loadProfileTree",
    config: [
      {
        key: "all",
        tableName: "load_profile_shapes",
        columnNames: "load",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energy, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "forwardPriceCurveTree",
    config: [
      {
        key: "all",
        tableName: "forward_price_curve_shapes",
        columnNames: "price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energy, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "weatherObservationTree",
    config: [
      {
        key: "max temp",
        tableName: "weather",
        columnNames: "MAX_TEMP",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.temperature, side: "right" },
      },
      {
        key: "min temp",
        tableName: "weather",
        columnNames: "MIN_TEMP",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.temperature, side: "right" },
      },
      {
        key: "wind",
        tableName: "weather",
        columnNames: "WIND",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.windSpeed, side: "right" },
      },
      {
        key: "solar",
        tableName: "weather",
        columnNames: "SOLAR",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.solarExposure, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "interconnectorsTree",
    config: [
      {
        key: "import limit",
        tableName: "interconnector",
        columnNames: "importlimit",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "export limit",
        tableName: "interconnector",
        columnNames: "exportlimit",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "flow",
        tableName: "interconnector",
        columnNames: "mwflow",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "SCADATree",
    config: [
      // Assets chart live SCADA, keyed by DUID. Kept first so selections saved
      // before the tree refactor — which carry no `variable` — still resolve
      // here, since getTreeConfig falls back to config[0].
      {
        key: "all",
        tableName: "scada",
        columnNames: "scadavalue",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      // An `All {region}` row spans every technology, which no column_name can
      // name, so it sends an empty one and filters by state alone. Needs the
      // matching API change — see coreau/planning#1918.
      {
        key: regionTotalVariable,
        tableName: "nem_generation",
        columnNames: null,
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      // Technology and below are unchanged: the technology is the column_name,
      // with state and sub-technology travelling in selected_item.
      {
        key: "battery",
        tableName: "nem_generation",
        columnNames: "battery",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "bioenergy",
        tableName: "nem_generation",
        columnNames: "bioenergy",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "coal",
        tableName: "nem_generation",
        columnNames: "coal",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "distillate",
        tableName: "nem_generation",
        columnNames: "distillate",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "gas",
        tableName: "nem_generation",
        columnNames: "gas",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "hydro",
        tableName: "nem_generation",
        columnNames: "hydro",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "solar",
        tableName: "nem_generation",
        columnNames: "solar",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "wind",
        tableName: "nem_generation",
        columnNames: "wind",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
      {
        key: "other",
        tableName: "nem_generation",
        columnNames: "other",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.power, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "asxProductTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energyVolume, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "hourlyShapesTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energyVolume, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "headsAndTailsTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energyVolume, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "productTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.energyVolume, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "productVWAPsTree",
    config: [
      {
        key: "vwap",
        tableName: "trading",
        columnNames: "vwap",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.price, side: "left" },
      },
    ],
    value: "id",
  },
  {
    treeName: "environmentalProductTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificateVolume, side: "right" },
      },
      {
        key: "strip trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificateVolume, side: "right" },
      },
      {
        key: "vwap",
        tableName: "trading",
        columnNames: "vwap",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "strip trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "agriculture",
        tableName: "certificate_product_premium_curves",
        columnNames: "1",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "energy efficiency",
        tableName: "certificate_product_premium_curves",
        columnNames: "2",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "industrial",
        tableName: "certificate_product_premium_curves",
        columnNames: "3",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "landfill gas capture",
        tableName: "certificate_product_premium_curves",
        columnNames: "4",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "savanna burning",
        tableName: "certificate_product_premium_curves",
        columnNames: "5",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "vegetation",
        tableName: "certificate_product_premium_curves",
        columnNames: "6",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "reforestation",
        tableName: "certificate_product_premium_curves",
        columnNames: "7",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "implied volatility curve",
        tableName: "iv_curve",
        columnNames: "volatility",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.volatility, side: "right" },
      },
    ],
    value: "id",
  },
  {
    treeName: "carbonProductTree",
    config: [
      {
        key: "benchmark",
        tableName: "trading",
        columnNames: "curve",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificateVolume, side: "right" },
      },
      {
        key: "strip trades (volume)",
        tableName: "trading",
        columnNames: "trade_volume",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificateVolume, side: "right" },
      },
      {
        key: "strip trades (price)",
        tableName: "trading",
        columnNames: "trade_price",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.certificatePrice, side: "right" },
      },
      {
        key: "implied volatility curve",
        tableName: "iv_curve",
        columnNames: "volatility",
        aggFunc: "avg",
        axis: { label: AXIS_LABELS.volatility, side: "right" },
      },
    ],
    value: "id",
  },
];
export const legacyNoneAggLabel: string = "All Data";
export const nemNoneAggLabel: string = "5 Min";

/**
 * `api.aggregates` buckets and sums on this period; `none` returns the source
 * rows untouched, which for the generation mix is one row per technology rather
 * than one per interval. See coreau/app!2908.
 */
export const fiveMinAggPeriod = "5min" as RpcAggPeriodType;
export const fiveMinAggFunc = "sum" as RpcAggFuncType;

export const aggOptions: AGGOptionType[] = [
  // `batch` is the slice the date range is chunked into for one request, and
  // PostgREST caps a response at 27,000 rows. 5-minutely data is 288 rows a
  // day, so a quarter is 26,496 — close enough to the cap to be truncated.
  { batch: "week", value: "none", label: legacyNoneAggLabel },
  { batch: "quarter", value: "half-hour", label: "30 Min" },
  { batch: "quarter", value: "day", label: "Daily" },
  { batch: "quarter", value: "month", label: "Monthly" },
  { batch: "quarter", value: "quarter", label: "Quarterly" },
  { batch: "year", value: "year", label: "Yearly" },
];

/**
 * Interval labels for the NEM accordions (Electricity, FCAS, Interconnectors,
 * SCADA), where "All Data" reads as "5 Min" — the actual granularity of NEM
 * dispatch data. The carbon, environmental and power trees keep `aggOptions`
 * because their underlying data is not 5-minutely.
 */
export const nemAggOptions: AGGOptionType[] = aggOptions.map((option) =>
  option.value === "none" ? { ...option, label: nemNoneAggLabel } : option
);

/**
 * The slice a date range is chunked into for one request. `5min` is not an
 * option in the tree, so it has no `batch` of its own — it carries the same
 * 5-minutely row count as the interval the tree calls "5 Min".
 */
export const aggRequestBatch = (
  aggPeriod?: RpcAggPeriodType
): unitOfTime.DurationConstructor => {
  const period = aggPeriod === fiveMinAggPeriod ? "none" : aggPeriod;
  return aggOptions.find(({ value }) => value === period)?.batch ?? "month";
};

export const aggFuncOptions: AGGFuncOptionType[] = [
  { value: "avg", label: "Average" },
  { value: "sum", label: "Sum" },
  { value: "max", label: "High" },
  { value: "min", label: "Low" },
  { value: "last", label: "Close" },
  { value: "first", label: "Open" },
];

export const defaultFunction: RpcAggFuncType = "avg";
export const defaultPeriod: RpcAggPeriodType = "half-hour";

const notEmpty = <TValue>(
  value: TValue | null | undefined
): value is TValue => {
  return value !== null && value !== undefined;
};

/**
 * Trace colour for a NEM - SCADA selection, reusing the same helpers as Asset
 * Intelligence so a technology keeps one colour across the product:
 * `getTechColour` for technology, sub-technology and asset traces, and the
 * market tracker's state palette for region-level aggregates.
 *
 * Returns undefined for every other tree, leaving `prepareChartColorV1` to fall
 * back to its positional palette.
 */
export const getScadaTraceColour = (
  treeName: string,
  selection: TreeSelectValue,
  isDark: boolean
): string | undefined => {
  if (treeName !== "SCADATree") return undefined;
  if (selection.technologyType) {
    return getTechColour(
      selection.technologyType,
      selection.technologySubType,
      isDark
    );
  }
  if (selection.stateCode) {
    return NEM_STATE_COLOURS[selection.stateCode][100];
  }
  return undefined;
};

/**
 * All bar traces share one alignment group so Plotly's per-subplot bar layout
 * pass allocates slots against the same x axis. Without it, bars sitting on
 * different (overlaying) y axes would be drawn on top of each other.
 */
const barAlignmentGroup = "chart-builder-bars";

/**
 * `barmode` is fixed (see ChartBuilderComponent); whether bars stack or sit side
 * by side is decided purely by how offset groups are named. Plotly buckets a
 * stack by (position, offsetgroup) — see `Sieve.getLabel` — so a group of one is
 * a bar on its own, and several series sharing a group become one stacked
 * column. Slot width is `barGroupWidth / <number of offset groups>`, which is
 * why stacking series widens the bars rather than thinning them.
 */
const getBarOffsetGroup = (
  traceStyle: TraceStyle,
  dataType: string,
  seriesKey: string
): string =>
  traceStyle === "barStacked" ? dataType : `${dataType}::${seriesKey}`;

type TracePlotlyStyle = Pick<
  Trace,
  "type" | "fill" | "stackgroup" | "alignmentgroup" | "offsetgroup"
>;

const traceStyleToPlotlyProps = (
  traceStyle: TraceStyle,
  seriesKey: string,
  dataType: string,
  isStackBaseline: boolean
): TracePlotlyStyle => {
  switch (traceStyle) {
    case "bar":
    case "barStacked":
      return {
        type: "bar",
        alignmentgroup: barAlignmentGroup,
        // Keyed by series (not by trace) so the two halves of a series split
        // across the today break stay in a single slot.
        offsetgroup: getBarOffsetGroup(traceStyle, dataType, seriesKey),
      };
    case "area":
      return { type: "scatter", fill: "tozeroy" };
    case "areaStacked":
      // One stack group per data type: plotly stacks per subplot, so each y
      // axis accumulates independently with no cross-axis interference.
      return {
        type: "scatter",
        stackgroup: dataType,
        fill: isStackBaseline ? "tozeroy" : "tonexty",
      };
    default:
      return { type: "scatter" };
  }
};

export const getTraceStyle = (
  dataType: string,
  dataTypeStyles?: DataTypeStyles
): TraceStyle => dataTypeStyles?.[dataType]?.traceStyle ?? defaultTraceStyle;

const getChartConfigurations = (
  allSelection: ChartSelect[],
  dataTypeStyles: DataTypeStyles | undefined,
  isDark: boolean
): Trace[] => {
  // Only the bottom trace of a stack group fills to zero; the rest fill to the
  // trace below them.
  const stackGroupsWithBaseline = new Set<string>();

  return allSelection
    .sort((a, b) => {
      const orderA = a.selection.order;
      const orderB = b.selection.order;
      if (orderA === undefined) return -1;
      if (orderB === undefined) return 1;
      return orderA - orderB;
    })
    .map(
      ({
        tree: treeName,
        aggPeriod,
        aggFunc,
        dateRange,
        selection: treeSelection,
      }: ChartSelect): Trace | null => {
        const selectionData = configTree.find(
          (item) => item.treeName === treeName
        );
        if (selectionData) {
          const treeConfig = getTreeConfig(treeSelection, selectionData);

          if (Object.keys(treeConfig).length === 0) return null;

          const tableName: RpcTableNameType = treeConfig.tableName;
          const columnName: RpcColumnNameType = treeConfig.columnNames;
          const treeValue: number = selectionData.hasOwnProperty("value")
            ? treeSelection[selectionData.value]
            : treeSelection;
          const axisTitle: string = treeConfig.axis.label;
          const axisSide: Axis["side"] =
            treeConfig.axis.side === "right" ? AxisRight : AxisLeft;
          const traceName = getTraceName(treeSelection, tableName, columnName);
          const traceColour = getScadaTraceColour(
            treeName,
            treeSelection,
            isDark
          );
          const traceStyle = getTraceStyle(axisTitle, dataTypeStyles);
          const isStackBaseline = !stackGroupsWithBaseline.has(axisTitle);
          stackGroupsWithBaseline.add(axisTitle);
          return {
            aggFunction: aggFunc || treeConfig.aggFunc || defaultFunction,
            aggPeriod: aggPeriod || defaultPeriod,
            dateRange: dateRange,
            ...traceStyleToPlotlyProps(
              traceStyle,
              traceName,
              axisTitle,
              isStackBaseline
            ),
            axis: {
              side: axisSide,
              title: axisTitle,
            },
            columnName: columnName,
            tableName: tableName,
            title: { text: traceName },
            name: traceName,
            selectedValue: treeValue,
            ...(traceColour && { "line.color": traceColour }),
          };
        } else {
          return null;
        }
      }
    )
    .filter(notEmpty);
};

export const generateChartSelection = (
  allSelection: ChartSelect[],
  dataTypeStyles?: DataTypeStyles,
  isDark: boolean = false
): Trace[] => {
  if (!allSelection) {
    return [];
  } else {
    return getChartConfigurations(allSelection, dataTypeStyles, isDark);
  }
};

/**
 * The data types the chart is currently plotting, in selection order.
 *
 * Derived from the traces the chart itself renders so the style sider can
 * never list a data type the chart does not have (or miss one it does).
 */
export const getActiveDataTypes = (traces: Trace[]): string[] =>
  Array.from(new Set(traces.map((trace) => trace.axis.title)));

const getTraceName = (
  treeSelection: TreeSelectionType,
  tableName: RpcTableNameType | undefined,
  columnName: RpcColumnNameType | undefined
) => {
  return typeof treeSelection === "object" &&
    treeSelection.hasOwnProperty("traceName")
    ? treeSelection.traceName
    : `${tableName || "Unknown"} - ${columnName || "Unknown"}`;
};

const getTreeConfig = (
  treeSelection: TreeSelectionType,
  selectionData
): {
  key: string;
  tableName: RpcTableNameType;
  columnNames: RpcColumnNameType;
  aggFunc: RpcAggFuncType;
  axis: { label: string; side: string };
} => {
  return treeSelection.hasOwnProperty("variable")
    ? selectionData?.config.find(
        (t) => t.key === treeSelection.variable?.toLowerCase()
      ) || {}
    : selectionData?.config[0];
};
export function getChartTitle(
  selections: ChartSelect[] | undefined,
  dateRange: RelativeRangeValue
): string {
  if (!selections || selections.length <= 0) return "Chart Builder";
  let configTitle;
  let selectionTraceLabel = "";
  let hasOthers = "";
  for (let ind = 0; ind < selections.length; ind++) {
    if (ind > 1) {
      hasOthers = ` + others`;
      break;
    }
    const treeSelection = selections[ind];
    const selectionData = configTree.find(
      (item) => item.treeName === treeSelection.tree
    );
    const treeConfig = getTreeConfig(treeSelection.selection, selectionData);
    const tableName: RpcTableNameType = treeConfig.tableName;
    const columnName: RpcColumnNameType = treeConfig.columnNames;
    const traceName = getTraceName(
      treeSelection.selection,
      tableName,
      columnName
    );

    if (treeSelection.tree === "weatherObservationTree") {
      selectionTraceLabel =
        ind > 0 ? `${selectionTraceLabel},<br> ${traceName}` : `${traceName}`;
    } else {
      selectionTraceLabel =
        ind > 0 ? `${selectionTraceLabel}, ${traceName}` : `${traceName}`;
    }
  }

  const [startDate, endDate] = getDateFromRelativeDateRange(dateRange) || [];
  configTitle = `${selectionTraceLabel} from ${startDate?.format(
    "YYYY-MM-DD"
  )} to ${endDate?.format("YYYY-MM-DD")}${hasOthers}`;
  return configTitle;
}

const transformCheckedKeysV2 = (
  checkedKeys: string[],
  aggPeriod?: RpcAggPeriodType,
  aggFunction?: RpcAggFuncType
): string[] => {
  return checkedKeys.map((key) => {
    let transformedKey = key;
    if (aggPeriod && aggFunction) {
      const periodLabel: string | undefined = aggOptions.find(
        (p) => p.value === aggPeriod
      )?.label;
      const funcLabel: string | undefined = aggFuncOptions.find(
        (p) => p.value === aggFunction
      )?.label;
      if (
        periodLabel &&
        funcLabel &&
        key.search(`-${periodLabel}-${funcLabel}`) === -1
      ) {
        const currentKeys = key.split("-");
        const lastElement = currentKeys.pop();
        transformedKey = `${currentKeys.join(
          "-"
        )}-${periodLabel}-${funcLabel}-${lastElement}`;
      }
    }
    return transformedKey;
  });
};

/**
 * Accordions whose interval labels use `nemAggOptions` — the "All Data" leaf
 * reads as "5 Min" in these, and only these.
 */
export const nemTreeNames: string[] = [
  "electricityTree",
  "FCASTree",
  "interconnectorsTree",
  "SCADATree",
];

const renameNoneAggKey = (key: string): string => {
  // Keys are `path.join("-")` with the interval label second-to-last and the
  // node value last, so index from the end — earlier path segments (station
  // names especially) can themselves contain "-".
  const parts = key.split("-");
  const labelIndex = parts.length - 2;
  if (parts[labelIndex] !== legacyNoneAggLabel) return key;
  parts[labelIndex] = nemNoneAggLabel;
  return parts.join("-");
};

const legacyNoneAggSuffix = ` - ${legacyNoneAggLabel}`;

const renameNoneAggTraceName = (traceName: string): string =>
  traceName.endsWith(legacyNoneAggSuffix)
    ? `${traceName.slice(0, -legacyNoneAggSuffix.length)} - ${nemNoneAggLabel}`
    : traceName;

/**
 * Charts saved before the 5 minute bucket existed ask for `none` on their SCADA
 * selections, which returns the generation mix as one row per technology rather
 * than one summed row per interval. Only the SCADA tree is rewritten: the other
 * NEM sources carry a single row per interval either way.
 */
export const migrateScadaFiveMinPeriod = (
  treeSelect: ChartBuilderTreeSelection
): ChartBuilderTreeSelection => {
  const tree = treeSelect["SCADATree"];
  if (!tree) return treeSelect;

  const toFiveMin = (values: TreeSelectValue[]): TreeSelectValue[] =>
    values.map((value) =>
      value.aggPeriod === "none"
        ? { ...value, aggPeriod: fiveMinAggPeriod, aggFunc: fiveMinAggFunc }
        : value
    );

  return {
    ...treeSelect,
    SCADATree: {
      ...tree,
      change: toFiveMin(tree.change ?? []),
      value: toFiveMin(tree.value ?? []),
    },
  };
};

/**
 * Charts saved before the NEM accordions renamed "All Data" to "5 Min" hold the
 * old label inside their tree keys and trace names. Without this the chart
 * still draws (traces come from the stored values, not the keys) but the tree
 * shows no ticks for those selections and the legend keeps the old wording.
 */
export const migrateNemIntervalLabels = (
  treeSelect: ChartBuilderTreeSelection
): ChartBuilderTreeSelection => {
  const migrated: ChartBuilderTreeSelection = { ...treeSelect };

  nemTreeNames.forEach((treeName) => {
    const tree = migrated[treeName];
    if (!tree) return;

    const renameValues = (values: TreeSelectValue[]): TreeSelectValue[] =>
      values.map((value) =>
        value.traceName
          ? { ...value, traceName: renameNoneAggTraceName(value.traceName) }
          : value
      );

    migrated[treeName] = {
      ...tree,
      checkedKeys: (tree.checkedKeys ?? []).map(renameNoneAggKey),
      change: renameValues(tree.change ?? []),
      value: renameValues(tree.value ?? []),
    };
  });

  return migrated;
};

const legacyBrokerCurveLabel = "Broker Curve";
const benchmarkLabel = "Benchmark";

/**
 * Trees whose "Broker Curve" leaf was renamed to "Benchmark".
 */
export const brokerCurveTreeNames: string[] = [
  "asxProductTree",
  "hourlyShapesTree",
  "productTree",
  "environmentalProductTree",
  "carbonProductTree",
];

// Real saved trace names carry a suffix after the variable (e.g. the
// aggregation label in "SMC CY 2026 Broker Curve - All Data"), so only
// matching a trailing " Broker Curve" misses almost every saved chart.
// Renaming the last occurrence instead still leaves an earlier, coincidental
// "Broker Curve" product name alone (see the "awkward" test case below).
const renameBrokerCurveTraceName = (traceName: string): string => {
  const index = traceName.lastIndexOf(legacyBrokerCurveLabel);
  if (index === -1) return traceName;
  return (
    traceName.slice(0, index) +
    benchmarkLabel +
    traceName.slice(index + legacyBrokerCurveLabel.length)
  );
};

const renameBrokerCurveKey = (key: string): string =>
  key
    .split("-")
    .map((part) => (part === legacyBrokerCurveLabel ? benchmarkLabel : part))
    .join("-");

/**
 * Charts saved before "Broker Curve" was renamed to "Benchmark" hold the old
 * label inside their tree keys, trace names and `variable` field. Without this
 * the chart still draws (traces come from the stored values) but the tree
 * shows no ticks for those selections, the legend keeps the old wording, and
 * the CSV-download feature check (keyed on `variable`) no longer matches.
 */
export const migrateBrokerCurveLabel = (
  treeSelect: ChartBuilderTreeSelection
): ChartBuilderTreeSelection => {
  const migrated: ChartBuilderTreeSelection = { ...treeSelect };

  brokerCurveTreeNames.forEach((treeName) => {
    const tree = migrated[treeName];
    if (!tree) return;

    const renameValues = (values: TreeSelectValue[]): TreeSelectValue[] =>
      values.map((value) => ({
        ...value,
        ...(value.variable === legacyBrokerCurveLabel && {
          variable: benchmarkLabel,
        }),
        ...(value.traceName && {
          traceName: renameBrokerCurveTraceName(value.traceName),
        }),
      }));

    migrated[treeName] = {
      ...tree,
      checkedKeys: (tree.checkedKeys ?? []).map(renameBrokerCurveKey),
      change: renameValues(tree.change ?? []),
      value: renameValues(tree.value ?? []),
    };
  });

  return migrated;
};

export const transformTreeSelectV2 = (
  treeSelect: ChartBuilderTreeSelection,
  aggPeriod?: RpcAggPeriodType,
  aggFunc?: RpcAggFuncType
) => {
  Object.keys(treeSelect).forEach((key) => {
    const checkTree = treeSelect[key];
    if (checkTree.value.length > 0) {
      const transformedData: TreeSelectValue[] = checkTree.value.map((item) => {
        return {
          ...item,
          aggPeriod: item.aggPeriod
            ? item.aggPeriod
            : aggPeriod || defaultPeriod,
          aggFunc: item.aggFunc ? item.aggFunc : aggFunc || defaultFunction,
        };
      });
      treeSelect[key].change = transformedData;
      treeSelect[key].value = transformedData;
      treeSelect[key].checkedKeys = treeSelect[key].checkedKeys
        ? transformCheckedKeysV2(
            treeSelect[key].checkedKeys,
            aggPeriod || defaultPeriod,
            aggFunc || defaultFunction
          )
        : [];
    }
  });
  return treeSelect;
};

export function useChartBuilder(apiConfig: TracesAPIConfig[]): ChartResponse {
  const apiOptions: TraceParam[] = apiConfig.map((ap) => ap.params);
  const chartOptions: Trace[] = apiConfig.map((ap) => ap.chartOptions);

  const apiOptionsNew: APIOptionType[] = apiOptions.map((o) => {
    const {
      params: { includeTimesWithoutData, by, ...otherParams },
    } = o;

    return {
      rangeOptions: o.rangeOptions,
      body: {
        includeTimesWithoutData: includeTimesWithoutData ?? false,
        ...(by !== undefined && { by: `by=${by}` }),
        ...otherParams,
      },
    };
  });

  const { results, progress, forceRefresh } = useAPIRange(
    "aggregates",
    apiOptionsNew
  );

  const { error, loading } = results;
  const responses = results.map(
    ({
      data,
      apiOption: {
        body: { aggFunction },
      },
    }) => {
      return { data, aggFunction };
    }
  );

  if (!loading && error) {
    console.error(error);
    message.error(
      "There was a error loading a chart. A refresh might fix the problem."
    );
  }

  useEffect(() => {
    const timer = setTimeout(() => forceRefresh(), 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [forceRefresh]);

  return {
    error,
    loading,
    progress,
    data: responses.map((r, i) => ({
      data: r ? r.data : undefined,
      chartOptions: chartOptions[i],
      aggFunc: r ? r.aggFunction : undefined,
    })),
  };
}
